/**
 * Time range picker with preset buttons and custom date picker.
 * Used in the monitoring dashboard to select the query time window.
 *
 * @module components/ui/TimeRangePicker
 */

import { useState, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';

export interface TimeRange {
  startTime: string;
  endTime: string;
}

interface Preset {
  label: string;
  hours: number;
}

const PRESETS: Preset[] = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '1d', hours: 24 },
  { label: '3d', hours: 72 },
  { label: '7d', hours: 168 },
];

function makePresetRange(hours: number): TimeRange {
  const end = new Date();
  const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
}

function toDatetimeLocal(isoString: string): string {
  const d = new Date(isoString);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}

export function TimeRangePicker({ value, onChange }: TimeRangePickerProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [activePreset, setActivePreset] = useState<string>('1d');

  const handlePreset = useCallback(
    (preset: Preset) => {
      setActivePreset(preset.label);
      setShowCustom(false);
      onChange(makePresetRange(preset.hours));
    },
    [onChange]
  );

  const handleCustomStart = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = new Date(e.target.value);
      if (!isNaN(d.getTime())) {
        setActivePreset('');
        onChange({ ...value, startTime: d.toISOString() });
      }
    },
    [onChange, value]
  );

  const handleCustomEnd = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = new Date(e.target.value);
      if (!isNaN(d.getTime())) {
        setActivePreset('');
        onChange({ ...value, endTime: d.toISOString() });
      }
    },
    [onChange, value]
  );

  const customStartLocal = useMemo(() => toDatetimeLocal(value.startTime), [value.startTime]);
  const customEndLocal = useMemo(() => toDatetimeLocal(value.endTime), [value.endTime]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex rounded-lg border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => handlePreset(preset)}
            className={clsx(
              'px-3 py-1.5 text-xs font-medium transition-colors',
              activePreset === preset.label
                ? 'bg-gray-900 dark:bg-white text-white dark:text-black'
                : 'bg-white dark:bg-[#0a0a0a] text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <button
        onClick={() => {
          setShowCustom(!showCustom);
          setActivePreset('');
        }}
        className={clsx(
          'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
          showCustom || !activePreset
            ? 'border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-black'
            : 'border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a] text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
        )}
      >
        Custom
      </button>

      {showCustom && (
        <div className="flex items-center gap-2 text-xs">
          <input
            type="datetime-local"
            value={customStartLocal}
            onChange={handleCustomStart}
            className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a] text-gray-900 dark:text-white text-xs"
          />
          <span className="text-gray-400">to</span>
          <input
            type="datetime-local"
            value={customEndLocal}
            onChange={handleCustomEnd}
            className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a] text-gray-900 dark:text-white text-xs"
          />
        </div>
      )}
    </div>
  );
}

/** Returns a default 1-day time range. */
// eslint-disable-next-line react-refresh/only-export-components
export function getDefaultTimeRange(): TimeRange {
  return makePresetRange(24);
}
